import { colorToHslChannels } from './color';

/** 颜色配置 */
export const colors = {
	mainColor: '#BA1120',
	shopMainColor: '#BA1120',
};

/**
 * 将camelCase转换为kebab-case
 * @param str camelCase字符串
 * @returns kebab-case字符串
 */
function camelToKebab(str: string): string {
	return str.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/**
 * 数值限制和格式化辅助函数
 */
function clampAndFormat(value: number, min: number, max: number): number {
	return Number(Math.max(min, Math.min(max, value)).toFixed(1));
}

/**
 * 处理色调循环特性（0-360度）
 */
function normalizeHue(hue: number): number {
	let normalized = hue;
	if (normalized < 0) {
		normalized += 360;
	} else if (normalized >= 360) {
		normalized -= 360;
	}
	return Number(normalized.toFixed(1));
}

/**
 * 通用CSS变量生成器
 */
function generateVariants(
	allVars: Record<string, string>,
	prefix: string,
	steps: number[],
	generator: (step: number, index: number) => { suffix: string; hsl: string },
): void {
	steps.forEach((step, index) => {
		const { suffix, hsl } = generator(step, index);
		allVars[`--${prefix}-${suffix}`] = hsl;
	});
}

/**
 * 根据颜色配置自动生成CSS变量
 * @param colors 颜色配置对象
 * @returns 包含所有CSS变量的对象
 */
function generateThemeColorVars(colors: Record<string, string>): Record<string, string> {
	const allVars: Record<string, string> = {};

	Object.entries(colors).forEach(([colorName, colorValue]) => {
		// 将colorName转换为kebab-case作为CSS变量前缀
		const prefix = camelToKebab(colorName);

		// 配置参数
		const config = {
			opacities: [8, 10, 20, 30, 40, 50, 60, 70, 80],
			lightSteps: [10, 20, 30],
			darkSteps: [10, 20, 30],
			saturationSteps: [5, 10, 15],
			hueSteps: [-45, -30, -15, 15, 30, 45],
		};

		// 转换为HSL通道
		const hslChannels = colorToHslChannels(colorValue).split(',');
		const [h, s, l] = hslChannels.map((channel) => channel.trim());

		// 解析数值（去掉%符号）
		const hNum = parseFloat(h);
		const sNum = parseFloat(s.replace('%', ''));
		const lNum = parseFloat(l.replace('%', ''));

		// 生成主题色变量
		allVars[`--${prefix}`] = `hsl(${h} ${s} ${l})`;
		allVars[`--${prefix}-hsl`] = `${h} ${s} ${l}`;

		// 生成透明度变量
		generateVariants(allVars, prefix, config.opacities, (opacity) => ({
			suffix: `${opacity}p`,
			hsl: `hsl(${h} ${s} ${l} / ${opacity}%)`,
		}));

		// 生成亮色变量：同时增加亮度和饱和度
		generateVariants(allVars, prefix, config.lightSteps, (lightStep, index) => {
			const saturationStep = config.saturationSteps[index] || 0;
			const newL = clampAndFormat(lNum + lightStep, 0, 100);
			const newS = clampAndFormat(sNum + saturationStep, 0, 100);
			return {
				suffix: `l-${lightStep}p`,
				hsl: `hsl(${h} ${newS}% ${newL}%)`,
			};
		});

		// 生成暗色变量：同时降低亮度和饱和度
		generateVariants(allVars, prefix, config.darkSteps, (darkStep, index) => {
			const saturationStep = config.saturationSteps[index] || 0;
			const newL = clampAndFormat(lNum - darkStep, 0, 100);
			const newS = clampAndFormat(sNum - saturationStep, 0, 100);
			return {
				suffix: `d-${darkStep}p`,
				hsl: `hsl(${h} ${newS}% ${newL}%)`,
			};
		});

		// 生成色调梯次变量
		generateVariants(allVars, prefix, config.hueSteps, (hueStep) => {
			const newH = normalizeHue(hNum + hueStep);
			const stepStr = hueStep > 0 ? `${hueStep}` : `${hueStep}`;
			return {
				suffix: `h${stepStr}p`,
				hsl: `hsl(${newH} ${s} ${l})`,
			};
		});
	});

	return allVars;
}

export const themeColorVars = generateThemeColorVars(colors);
